import React, { useState, Fragment } from "react";
import {
  FaFacebookMessenger,
  FaBell,
  FaPlus,
  FaHome,
  FaSearch,
  FaTrash,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";

const Sidebar = ({
  channels,
  activeChannelId,
  setActiveChannelId,
  openModal,
  openConfirmModal,
  channelMembersDetails,
  currentUser,
  activeProject,
}) => {
  const [notificationsDisabled, setNotificationsDisabled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleToggleNotifications = () => {
    setIsModalOpen(true);
  };

  const confirmToggleNotifications = () => {
    setNotificationsDisabled(!notificationsDisabled);
    setIsModalOpen(false);
  };

  return (
    <div className="w-1/4 h-[90vh] bg-white p-4 border-r flex flex-col overflow-hidden">
      <div className="text-lg font-semibold text-gray-800 mb-2">Channels</div>
      <div className="mb-4 overflow-y-auto flex-1">
        {channels.map((channel) => (
          <div
            key={channel.id}
            className={`flex justify-between items-center p-3 mt-2 rounded-md bg-gray-100 hover:bg-gray-300 cursor-pointer ${
              activeChannelId === channel.id ? "bg-gray-300" : ""
            }`}
            onClick={() => setActiveChannelId(channel.id)}
          >
            {channel.name}
            {(currentUser.uid === channel.createdBy ||
              currentUser.uid === activeProject?.creator?.uid) && (
              <FaTrash
                className="text-red-500 hover:text-red-700 ml-2"
                onClick={(e) => {
                  e.stopPropagation();
                  openConfirmModal(channel.id);
                }}
              />
            )}
          </div>
        ))}
      </div>

      {activeChannelId && (
        <div className="p-4 mt-4 mb-4 bg-white border-t border-gray-300">
          <h3 className="text-md font-semibold">Members</h3>
          <ul className="mt-2">
            {channelMembersDetails.map((member) => (
              <li key={member.id} className="p-1 flex items-center">
                <img
                  src={
                    member.profileImage || "https://via.placeholder.com/40x40"
                  }
                  alt="Profile"
                  className="w-7 h-7 rounded-full mr-2"
                />
                <span>{member.displayName || "Anonymous"}</span>
                <span
                  className={`ml-2 h-2 w-2 rounded-full ${
                    member.isOnline ? "bg-green-500" : "bg-gray-400"
                  }`}
                  title={member.isOnline ? "Online" : "Offline"}
                ></span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center justify-center space-x-4">
        <button
          className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center"
          onClick={() => navigate("/communication/dms")}
        >
          <FaFacebookMessenger />
        </button>
        <button
          className={`w-10 h-10 rounded-full flex items-center justify-center ${
            notificationsDisabled ? "bg-red-200" : "bg-gray-200"
          }`}
          onClick={handleToggleNotifications}
        >
          <FaBell />
        </button>
        <button
          className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center"
          onClick={openModal}
        >
          <FaPlus />
        </button>
        <button
          className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center"
          onClick={() => navigate("/")}
        >
          <FaHome />
        </button>
        <button className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
          <FaSearch />
        </button>
      </div>

      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => setIsModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    {notificationsDisabled
                      ? "Enable Notifications"
                      : "Disable Notifications"}
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-md text-gray-500">
                      {notificationsDisabled
                        ? "Are you sure you want to enable notifications for this project's channels?"
                        : "Are you sure you want to disable notifications for this project's channels?"}
                    </p>
                  </div>

                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                      onClick={confirmToggleNotifications}
                    >
                      {notificationsDisabled ? "Enable" : "Disable"}
                    </button>
                    <button
                      type="button"
                      className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                      onClick={() => setIsModalOpen(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default Sidebar;
